import React from "react";
import PropTypes from "prop-types";
import { Utilities } from "@learningblocks/exp-constants";
import { ContentDropdown } from "../../ContentDropdown";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { MainTitle } from "../Components/MainTitle";
import { QuestionImage } from "../Components/QuestionImage";
import { Footer } from "../Components/Footer";
import { TotalAttempts } from "../Components/TotalAttempts";
import { SubmitButton } from "../Components/SubmitButton";
import { Question } from "../Components/Question";
import { QuestionFeedback } from "../Components/QuestionFeedback";
import { Option } from "./Option";
import { useIntl } from "react-intl";
import "./style.scss";

const { QUESTION_CONTENTS } = Utilities;

/**
 * Experimental-Design Question component [Multi-Select]:
 *  - You will have n choices to select from
 *  - Your question will have m correct otions
 *  - m will be less than n.
 */
export function MultiSelect(props) {
  const intl = useIntl();
  const { allowReset = false } = props;

  // Notify parent when completion status changes
  React.useEffect(() => {
    if (props.onCompleteChange) {
      props.onCompleteChange(props.complete);
    }
  }, [props.complete]);

  const hasUnsubmittedOptions = () => {
    return props.checkboxes.reduce((boolVal, checkboxOption) => {
      if (
        checkboxOption.disabled === false &&
        checkboxOption.selected === true
      ) {
        boolVal = true;
      }
      return boolVal;
    }, false);
  };

  const checkAllCorrectAnswersSelected = () => {
    // Get all disabled (submitted) option IDs - this happens AFTER submit
    const disabledIds = props.checkboxes
      .filter((checkbox) => checkbox.disabled)
      .map((checkbox) => checkbox.id);

    // Check if all correct answers are in disabled list (were submitted)
    const allCorrectSubmitted = props.answerId.every((correctId) =>
      disabledIds.includes(correctId)
    );

    // Check if ONLY correct answers are disabled (no incorrect submissions)
    const onlyCorrectSubmitted = disabledIds.every((disabledId) =>
      props.answerId.includes(disabledId)
    );

    return (
      allCorrectSubmitted && onlyCorrectSubmitted && disabledIds.length > 0
    );
  };

  const feedback = props.feedback || {};

  if (!feedback["SUCCESS_MESSAGE"]) {
    feedback["SUCCESS_MESSAGE"] = intl.formatMessage({
      id: "Correct!",
      defaultMessage: "Correct!",
      description: "",
    });
  }

  if (!feedback["FAIL_MESSAGE"]) {
    feedback["FAIL_MESSAGE"] = intl.formatMessage({
      id: "Not quite. You haven't selected the correct combination of answers.",
      defaultMessage: "Not quite. You haven't selected the correct combination of answers.",
      description: "",
    });
  }

  const setSubmitDisabled = () => {
    const isQuestionComplete = checkAllCorrectAnswersSelected();

    // If question is complete and reset is not allowed, disable the button
    if (isQuestionComplete && !allowReset) {
      return true;
    }

    // Check if there are disabled options (indicating we're in "Try again" or "Reset" mode)
    const hasDisabledOptions = props.checkboxes.some(
      (checkbox) => checkbox.disabled
    );

    // If question is complete and reset allowed, or in "Try again" mode, button should be enabled
    if (isQuestionComplete || hasDisabledOptions) {
      return false;
    }

    // Otherwise, only disable if no options are selected (initial state)
    return hasUnsubmittedOptions() === false;
  };

  const getIsCorrect = ({ id }) => {
    // Only show correct/incorrect feedback if all correct answers are selected
    if (!checkAllCorrectAnswersSelected()) {
      return false; // Mark as incorrect if not all correct options are selected
    }
    return props.answerId.includes(id);
  };

  const onSubmit = () => {
    // If question is complete and "Reset question" button is clicked
    const isQuestionComplete = checkAllCorrectAnswersSelected();

    if (isQuestionComplete) {
      // Completely reset the question
      if (props.resetAnswer) {
        props.resetAnswer(props.id, props.type);
      }
      return;
    }

    // If in "Try again" mode, clear all selections
    const hasDisabledOptions = props.checkboxes.some(
      (checkbox) => checkbox.disabled
    );

    if (hasDisabledOptions && !isQuestionComplete) {
      // Clear all disabled states (unselect all)
      props.enableAllOptions(props.id);
    } else {
      // Normal submit
      props.submitAnswer(props.id, props.type);
    }
  };

  const selectOption = (params) => {
    const isQuestionComplete = checkAllCorrectAnswersSelected();

    // Don't allow selection if question is complete
    if (isQuestionComplete) {
      return;
    }

    // Check if we're in "Try again" mode (has disabled options)
    const hasDisabledOptions = props.checkboxes.some(
      (checkbox) => checkbox.disabled
    );

    // If in "Try again" mode, preserve selections but toggle the clicked option
    if (hasDisabledOptions) {
      // Get currently selected option IDs
      const currentlySelected = props.checkboxes
        .filter((checkbox) => checkbox.selected)
        .map((checkbox) => checkbox.id);

      // Determine new selection state: toggle the clicked option
      const newSelected = currentlySelected.includes(params.optionID)
        ? currentlySelected.filter((id) => id !== params.optionID)
        : [...currentlySelected, params.optionID];

      // Clear all disabled states
      props.enableAllOptions(props.id);

      // Re-select all options that should be selected
      newSelected.forEach((optionID) => {
        props.selectOption({ optionID, checked: true, componentID: props.id });
      });
      return;
    }

    // Process the selection normally
    props.selectOption({ ...params, componentID: props.id });
  };

  const getSubmitButtonClassName = () => {
    if (checkAllCorrectAnswersSelected()) {
      // Use reset styling if reset is allowed, otherwise use default disabled styling
      if (allowReset) {
        return "mscq__submit-button mscq__submit-button--reset";
      }
    }
    // Check if there are disabled options (indicating we're in "Try again" mode)
    const hasDisabledOptions = props.checkboxes.some(
      (checkbox) => checkbox.disabled
    );
    if (hasDisabledOptions && !checkAllCorrectAnswersSelected()) {
      return "mscq__submit-button mscq__submit-button--try-again";
    }
    return "mscq__submit-button";
  };

  const getSubmitButtonLabel = () => {
    // If all correct answers are selected
    if (checkAllCorrectAnswersSelected()) {
      // Show "Reset question" if reset is allowed, otherwise "Submit answer"
      if (allowReset) {
        return intl.formatMessage({
          id: "Reset question",
          defaultMessage: "Reset question",
          description: "",
        });
      }
    }

    // Check if there are disabled options (indicating we're in "Try again" mode)
    const hasDisabledOptions = props.checkboxes.some(
      (checkbox) => checkbox.disabled
    );

    // If there are disabled options and question is not complete, show "Try again"
    if (hasDisabledOptions && !checkAllCorrectAnswersSelected()) {
      return intl.formatMessage({
        id: "Try again",
        defaultMessage: "Try again",
        description: "",
      });
    }

    return intl.formatMessage({
      id: "Submit answer",
      defaultMessage: "Submit answer",
      description: "",
    });
  };

  const shouldShowGlobalFeedback = () => {
    return props.checkboxes.some((checkbox) => checkbox.disabled);
  };

  const isGloballyCorrect = () => {
    return checkAllCorrectAnswersSelected();
  };

  const renderCheckboxOptions = (key, options) => {
    return (
      <>
        {options.map((option, id) => {
          return (
            <Option
              key={options.id}
              {...option}
              index={id}
              tabIndex={"0"}
              selectOption={selectOption}
              isCorrect={getIsCorrect(option)}
            />
          );
        })}
        <QuestionFeedback
          feedback={feedback}
          key={key}
          tabIndex={"0"}
          answerId={props.answerId}
          checkboxes={props.checkboxes}
          className="mscq__answerfeedback"
          showFeedback={shouldShowGlobalFeedback()}
          complete={isGloballyCorrect()}
        />
      </>
    );
  };

  const renderImage = (key, image) => {
    if (image.src !== "") {
      return (
        <QuestionImage
          tabIndex="0"
          key={key}
          image={image}
          className="mscq__image"
          preview={props.preview}
        />
      );
    } else return null;
  };

  const renderQuestion = (key) => (
    <Question
      tabIndex="0"
      key={key}
      text={props.question}
      className="mscq__question"
      addMultiSelectText={true}
    />
  );

  const renderText = (key, text) => (
    <Question tabIndex={"0"} key={key} text={text} className="cscq__content" />
  );

  const hintDetailsSection = (key, hintDetails) => {
    return <ContentDropdown key={key} contentDetails={hintDetails} />;
  };

  const renderByType = (type, key, content) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.IMAGE:
        return renderImage(key, content);
      case QUESTION_CONTENTS.CHECKBOXES:
        return renderCheckboxOptions(key, props.checkboxes);
      case QUESTION_CONTENTS.HINTS:
        return hintDetailsSection(key, props.hintDetails);
      default:
        return null;
    }
  };

  const renderContents = () =>
    props.content.map((type, key) => renderByType(type, key, props[type]));

  const wrapperClass = ` mscq ${props.className}`;
  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle
        title={intl.formatMessage({
          id: "multi-select question",
          defaultMessage: "multi-select question",
          description: "",
        })}
        className="mscq__mainTitle"
      />
      {renderContents()}
      <Footer className="mscq__footer">
        {props.showAttempts && (
          <TotalAttempts
            totalAttempts={props.totalAttempts}
            className="mscq__footer__totalAttempts"
          />
        )}
        <SubmitButton
          label={getSubmitButtonLabel()}
          disabled={setSubmitDisabled()}
          onSubmit={onSubmit}
          className={getSubmitButtonClassName()}
        />
      </Footer>
    </QuestionWrapper>
  );
}

MultiSelect.propTypes = {
  /**
   * The list of ID's for the correct Options
   */
  answerId: PropTypes.arrayOf(PropTypes.number).isRequired,
  /**
   * The list of options.
   */
  checkboxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.string,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * The prop to indicate if the user has attempted the question before
   */
  hasAttempted: PropTypes.bool,
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold image data
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.string.isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that controls whether to show attempts counter
   */
  showAttempts: PropTypes.bool,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(["MSCQ"]),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * The callback prop executed when reset is clicked
   */
  resetAnswer: PropTypes.func,
  /**
   * The callback to be executed when an option is selected
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * The callback to enable all disabled options
   */
  enableAllOptions: PropTypes.func.isRequired,
  /**
   * This is the details that are passed to the hint
   */
  hintDetails: PropTypes.shape({
    /**
     * The label that is provided to the Hint section, usually Hint
     */
    label: PropTypes.string.isRequired,
    /**
     * Description details that is given to the hint
     */
    description: PropTypes.string.isRequired,
    /**
     * To show the question title
     */
    title: PropTypes.string,
  }),
  preview: PropTypes.bool,
  /**
   * Custom feedback messages for SUCCESS and FAIL states
   */
  feedback: PropTypes.shape({
    SUCCESS_MESSAGE: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    FAIL_MESSAGE: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  /**
   * Allow resetting the question after completion
   */
  allowReset: PropTypes.bool,
  /**
   * Callback fired when completion status changes
   */
  onCompleteChange: PropTypes.func,
};

MultiSelect.defaultProps = {
  className: "",
  type: "MSCQ",
  checkboxes: [],
  complete: false,
  content: [],
  image: "",
  question: "",
  totalAttempts: 0,
  showAttempts: false,
  answerId: [],
  preview: false,
  feedback: null,
  hasAttempted: false,
  enableAllOptions: () => {},
  resetAnswer: () => {},
  allowReset: false,
};
